import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { functions, storage } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Wrench, Loader2, Camera, CheckCircle, AlertTriangle } from 'lucide-react';

async function uploadFoto(file, empresaId, prefix) {
  const ts = Date.now();
  const ext = file.name.includes('.') ? file.name.split('.').pop() : 'jpg';
  const path = `equipamentos_manutencao/${empresaId}/${prefix}_${ts}.${ext}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  return { url, storagePath: path, uploadedAt: new Date().toISOString() };
}

function formatData(d) {
  if (!d) return '—';
  const dt = d.toDate ? d.toDate() : new Date(d);
  return dt.toLocaleDateString('pt-BR');
}

const PlanoManutencaoPage = () => {
  const { getEmpresaId, hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [execItem, setExecItem] = useState(null);
  const [obs, setObs] = useState('');
  const [fotosAntes, setFotosAntes] = useState([]);
  const [fotosDepois, setFotosDepois] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await httpsCallable(functions, 'listAgendaManutencao')({
        empresa_id: getEmpresaId(),
      });
      setItems(r.data?.data?.items || []);
    } catch (e) {
      setErr(e.message || 'Erro ao carregar agenda');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [getEmpresaId]);

  const handleFile = async (e, tipo) => {
    const files = Array.from(e.target.files || []);
    const empresaId = getEmpresaId();
    const uploaded = [];
    for (const f of files) {
      uploaded.push(await uploadFoto(f, empresaId, tipo));
    }
    if (tipo === 'antes') setFotosAntes((prev) => [...prev, ...uploaded]);
    else setFotosDepois((prev) => [...prev, ...uploaded]);
  };

  const executar = async () => {
    if (!execItem) return;
    setSaving(true);
    setErr('');
    try {
      await httpsCallable(functions, 'executarManutencao')({
        empresa_id: getEmpresaId(),
        plano_id: execItem.id,
        observacoes: obs,
        fotos_antes: fotosAntes,
        fotos_depois: fotosDepois,
      });
      setMsg('Manutenção registrada com sucesso.');
      setExecItem(null);
      setObs('');
      setFotosAntes([]);
      setFotosDepois([]);
      await load();
    } catch (e) {
      setErr(e.message || 'Erro ao registrar');
    } finally {
      setSaving(false);
    }
  };

  if (!hasPermission('manage_equipment')) {
    return <div className="p-6 text-center text-gray-600">Sem permissão.</div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Wrench className="h-7 w-7 text-amber-600" />
          Plano de Manutenção
        </h1>
        <p className="text-sm text-gray-600 mt-1">Manutenções ordenadas pela data mais próxima. Vencidas aparecem primeiro.</p>
      </div>

      {msg && <div className="text-sm text-green-800 bg-green-50 border rounded-lg px-3 py-2">{msg}</div>}
      {err && <div className="text-sm text-red-800 bg-red-50 border rounded-lg px-3 py-2">{err}</div>}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-500 border border-dashed rounded-xl">
          Nenhum plano de manutenção ativo. Cadastre planos nos equipamentos.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <li
              key={it.id}
              className={`border rounded-xl p-4 flex flex-wrap justify-between gap-3 ${
                it.vencida ? 'border-red-300 bg-red-50' : 'bg-white'
              }`}
            >
              <div>
                <div className="flex items-center gap-2">
                  {it.vencida ? (
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                  ) : null}
                  <h3 className="font-semibold text-gray-900">{it.titulo}</h3>
                </div>
                <p className="text-sm text-gray-600">{it.equipamento_apelido}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Próxima data: {formatData(it.proxima_data)}
                  {it.horas_restantes != null && ` · Horas restantes: ${it.horas_restantes.toFixed(1)} h`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setExecItem(it);
                  setObs('');
                  setFotosAntes([]);
                  setFotosDepois([]);
                }}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Executar
              </button>
            </li>
          ))}
        </ul>
      )}

      {execItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold">Executar: {execItem.titulo}</h2>
            <p className="text-sm text-gray-600">{execItem.equipamento_apelido}</p>
            <div>
              <label className="text-xs text-gray-500">Observações</label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 text-sm min-h-[80px]"
                value={obs}
                onChange={(e) => setObs(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 flex items-center gap-1">
                <Camera className="h-3 w-3" /> Fotos antes
              </label>
              <input type="file" accept="image/*" multiple onChange={(e) => handleFile(e, 'antes')} className="text-sm" />
              <p className="text-xs text-gray-400">{fotosAntes.length} arquivo(s)</p>
            </div>
            <div>
              <label className="text-xs text-gray-500 flex items-center gap-1">
                <Camera className="h-3 w-3" /> Fotos depois
              </label>
              <input type="file" accept="image/*" multiple onChange={(e) => handleFile(e, 'depois')} className="text-sm" />
              <p className="text-xs text-gray-400">{fotosDepois.length} arquivo(s)</p>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setExecItem(null)} className="px-3 py-2 border rounded-lg">
                Cancelar
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={executar}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg flex items-center gap-2"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Registrar execução
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanoManutencaoPage;
